/** @jsxImportSource @emotion/react */
import { useState } from 'react';
import { Box, Typography, Button, Paper, Modal, IconButton, Grid, Link, FormControl, InputLabel, Select, MenuItem, TextField, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { useMutation } from '@apollo/client';
import { CloseOutlined } from '@mui/icons-material';
import { Mutations } from 'services/apollo';
import { ConfigureDemoCaseMutation, CaseTypes } from 'services/GraphQl';
import { useSettings } from "contexts/Settings";

interface CaseDetails {
  id: string;
  companyId: string;
  caseId: string;
  contact?: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    address?: {
      zip?: string;
    };
  };
  whenBenefit?: {
    amount?: number;
  };
}

enum WhenBenefitType {
  NONE = 'NONE',
  LUMP_SUM = 'LUMP_SUM',
  MONTHLY_INSTALLMENT = 'MONTHLY_INSTALLMENT'
}

enum CoverageTier {
  EMPLOYEE_ONLY = 'EMPLOYEE_ONLY',
  EMPLOYEE_SPOUSE = 'EMPLOYEE_SPOUSE',
  EMPLOYEE_CHILDREN = 'EMPLOYEE_CHILDREN',
  EMPLOYEE_FAMILY = 'EMPLOYEE_FAMILY'
}

const DemoConfiguration = () => {
  const [open, setOpen] = useState(false);
  const [caseDetails, setCaseDetails] = useState<CaseDetails | null>(null);
  const [caseType, setCaseType] = useState<CaseTypes>(CaseTypes.TerminationVoluntary);
  const [whenBenefitType, setWhenBenefitType] = useState<WhenBenefitType>(WhenBenefitType.NONE);
  const [lumpSumAmount, setLumpSumAmount] = useState<number>(2000);
  const [monthlyAmount, setMonthlyAmount] = useState<number>(500);
  const [numberOfMonths, setNumberOfMonths] = useState<number>(12);
  const [ichraCoverageTier, setIchraCoverageTier] = useState<CoverageTier>(CoverageTier.EMPLOYEE_ONLY);
  const [ichraAmounts, setIchraAmounts] = useState({
    [CoverageTier.EMPLOYEE_ONLY]: 500,
    [CoverageTier.EMPLOYEE_SPOUSE]: 750,
    [CoverageTier.EMPLOYEE_CHILDREN]: 650,
    [CoverageTier.EMPLOYEE_FAMILY]: 900
  });
  const { settings } = useSettings();

  const [configureDemoCase, { loading }] = useMutation(Mutations.configureDemoCase, {
    onCompleted: (data: ConfigureDemoCaseMutation) => {
      setCaseDetails(data.configureDemoCase as CaseDetails);
      setOpen(true);
    },
    onError: (error) => {
      console.error('Error configuring demo case:', error);
    }
  });

  const handleClose = () => {
    setOpen(false);
  };

  // Calculate the case URL using environment variable and case data
  const getStagingCaseUrl = (companyId: string, caseId: string) => {
    const baseUrl = settings.REACT_APP_WHEN_APP_URL || '';
    return `${baseUrl}/exit/${companyId}/${caseId}`;
  };
  const getLovableCaseUrl = (companyId: string, caseId: string) => {
    return `https://demo.forwhen.com/exit/${companyId}/${caseId}`;
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Paper sx={{ padding: 3 }}>
        <Typography variant="h4" gutterBottom>
          Demo Configuration
        </Typography>
        <Typography variant="body1" paragraph>
          Use this page to configure demo cases for the sales team. When you click the button below, a pre-defined demo case will be generated.
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="case-type-label">Case Type</InputLabel>
          <Select
            labelId="case-type-label"
            id="case-type-select"
            value={caseType}
            label="Case Type"
            onChange={(e) => setCaseType(e.target.value as CaseTypes)}
            className="font-inter"
          >
            <MenuItem value={CaseTypes.TerminationVoluntary} className="font-inter">Termination (Voluntary)</MenuItem>
            <MenuItem value={CaseTypes.TerminationInvoluntary} className="font-inter">Termination (Involuntary)</MenuItem>
            <MenuItem value={CaseTypes.ActiveEmployee} className="font-inter">Active Employee</MenuItem>
            <MenuItem value={CaseTypes.CobraRenewal} className="font-inter">COBRA Renewal</MenuItem>
            <MenuItem value={CaseTypes.Ichra} className="font-inter">ICHRA</MenuItem>
            <MenuItem value={CaseTypes.OtherCobraEvent} className="font-inter">Other COBRA Event</MenuItem>
            <MenuItem value={CaseTypes.Student} className="font-inter">Student</MenuItem>
          </Select>
        </FormControl>

        {/* ICHRA Configuration */}
        {caseType === CaseTypes.Ichra && (
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom className="font-inter">
              ICHRA Contribution Configuration (Plan Year 2026)
            </Typography>

            <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
              <InputLabel id="coverage-tier-label">Coverage Tier</InputLabel>
              <Select
                labelId="coverage-tier-label"
                value={ichraCoverageTier}
                label="Coverage Tier"
                onChange={(e) => setIchraCoverageTier(e.target.value as CoverageTier)}
                className="font-inter"
              >
                <MenuItem value={CoverageTier.EMPLOYEE_ONLY} className="font-inter">Employee Only</MenuItem>
                <MenuItem value={CoverageTier.EMPLOYEE_SPOUSE} className="font-inter">Employee + Spouse</MenuItem>
                <MenuItem value={CoverageTier.EMPLOYEE_CHILDREN} className="font-inter">Employee + Children</MenuItem>
                <MenuItem value={CoverageTier.EMPLOYEE_FAMILY} className="font-inter">Employee + Family</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle1" gutterBottom className="font-inter">
              Monthly Contribution Amounts by Tier:
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Employee Only ($)"
                  type="number"
                  value={ichraAmounts[CoverageTier.EMPLOYEE_ONLY]}
                  onChange={(e) => setIchraAmounts({...ichraAmounts, [CoverageTier.EMPLOYEE_ONLY]: Number(e.target.value)})}
                  fullWidth
                  InputLabelProps={{ className: "font-inter" }}
                  inputProps={{ className: "font-inter", min: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Employee + Spouse ($)"
                  type="number"
                  value={ichraAmounts[CoverageTier.EMPLOYEE_SPOUSE]}
                  onChange={(e) => setIchraAmounts({...ichraAmounts, [CoverageTier.EMPLOYEE_SPOUSE]: Number(e.target.value)})}
                  fullWidth
                  InputLabelProps={{ className: "font-inter" }}
                  inputProps={{ className: "font-inter", min: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Employee + Children ($)"
                  type="number"
                  value={ichraAmounts[CoverageTier.EMPLOYEE_CHILDREN]}
                  onChange={(e) => setIchraAmounts({...ichraAmounts, [CoverageTier.EMPLOYEE_CHILDREN]: Number(e.target.value)})}
                  fullWidth
                  InputLabelProps={{ className: "font-inter" }}
                  inputProps={{ className: "font-inter", min: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Employee + Family ($)"
                  type="number"
                  value={ichraAmounts[CoverageTier.EMPLOYEE_FAMILY]}
                  onChange={(e) => setIchraAmounts({...ichraAmounts, [CoverageTier.EMPLOYEE_FAMILY]: Number(e.target.value)})}
                  fullWidth
                  InputLabelProps={{ className: "font-inter" }}
                  inputProps={{ className: "font-inter", min: 0 }}
                />
              </Grid>
            </Grid>

            <Typography variant="caption" display="block" sx={{ mt: 2 }} className="font-inter">
              Selected tier: <strong>{ichraCoverageTier.replace('_', ' + ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}</strong> -
              Monthly contribution: <strong>${ichraAmounts[ichraCoverageTier]}</strong>
            </Typography>
          </Paper>
        )}

        {/* When Benefit Configuration - Only show if not ICHRA */}
        {caseType !== CaseTypes.Ichra && (
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom className="font-inter">
              When Benefit Configuration
            </Typography>
            
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <RadioGroup
                value={whenBenefitType}
                onChange={(e) => setWhenBenefitType(e.target.value as WhenBenefitType)}
              >
                <FormControlLabel 
                  value={WhenBenefitType.NONE} 
                  control={<Radio />} 
                  label={<span className="font-inter">None - No When Benefit</span>}
                />
                <FormControlLabel 
                  value={WhenBenefitType.LUMP_SUM} 
                  control={<Radio />} 
                  label={<span className="font-inter">Lump Sum</span>}
                />
                <FormControlLabel 
                  value={WhenBenefitType.MONTHLY_INSTALLMENT} 
                  control={<Radio />} 
                  label={<span className="font-inter">Monthly Installment</span>}
                />
              </RadioGroup>
            </FormControl>

            {/* Lump Sum Configuration */}
            {whenBenefitType === WhenBenefitType.LUMP_SUM && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="Amount ($)"
                  type="number"
                  value={lumpSumAmount}
                  onChange={(e) => setLumpSumAmount(Number(e.target.value))}
                  fullWidth
                  InputLabelProps={{ className: "font-inter" }}
                  inputProps={{ className: "font-inter", min: 0 }}
                />
              </Box>
            )}

            {/* Monthly Installment Configuration */}
            {whenBenefitType === WhenBenefitType.MONTHLY_INSTALLMENT && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    label="Monthly Amount ($)"
                    type="number"
                    value={monthlyAmount}
                    onChange={(e) => setMonthlyAmount(Number(e.target.value))}
                    fullWidth
                    InputLabelProps={{ className: "font-inter" }}
                    inputProps={{ className: "font-inter", min: 0 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Number of Months"
                    type="number"
                    value={numberOfMonths}
                    onChange={(e) => setNumberOfMonths(Number(e.target.value))}
                    fullWidth
                    InputLabelProps={{ className: "font-inter" }}
                    inputProps={{ className: "font-inter", min: 1 }}
                  />
                </Grid>
              </Grid>
            )}
          </Paper>
        )}

        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => {
            const variables: any = { 
              caseType
            };
            
            // Add ICHRA configuration - pass it through whenBenefit
            if (caseType === CaseTypes.Ichra) {
              // For ICHRA, we use the whenBenefit field to pass contribution amount
              variables.whenBenefit = {
                hasBenefit: true,
                amount: ichraAmounts[ichraCoverageTier],
                total: ichraAmounts[ichraCoverageTier],
                distributionType: 'LUMP_SUM',
                status: 'AVAILABLE',
                type: 'ICHRA',
                releasedDate: new Date().toISOString(),
                detail: {
                  total: ichraAmounts[ichraCoverageTier],
                  amountSpent: 0,
                  amountPending: 0,
                  amountRemaining: ichraAmounts[ichraCoverageTier],
                  amountForfeited: 0
                }
              };
            }
            // Add whenBenefit configuration if not NONE and not ICHRA
            else if (whenBenefitType !== WhenBenefitType.NONE) {
              if (whenBenefitType === WhenBenefitType.LUMP_SUM) {
                variables.whenBenefit = {
                  distributionType: 'LUMP_SUM',
                  total: lumpSumAmount,
                  amount: lumpSumAmount,
                  hasBenefit: true,
                  status: 'AVAILABLE',
                  type: 'HEALTHCARE',
                  months: [],
                  reimbursementRequests: [],
                  releasedDate: new Date().toISOString(),
                  detail: {
                    total: lumpSumAmount,
                    amountSpent: 0,
                    amountPending: 0,
                    amountRemaining: lumpSumAmount,
                    amountForfeited: 0
                  }
                };
              } else if (whenBenefitType === WhenBenefitType.MONTHLY_INSTALLMENT) {
                const totalAmount = monthlyAmount * numberOfMonths;
                const months = Array.from({ length: numberOfMonths }, (_, index) => {
                  const monthDate = new Date();
                  monthDate.setMonth(monthDate.getMonth() + index);
                  return {
                    month: monthDate.toISOString().slice(0, 7), // YYYY-MM format
                    amount: monthlyAmount,
                    deadline: new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).toISOString(),
                    requested: 0,
                    reimbursedAmount: 0,
                    expired: 0,
                    returned: 0
                  };
                });
                
                variables.whenBenefit = {
                  distributionType: 'MONTHLY_ALLOCATION',
                  total: totalAmount,
                  amount: totalAmount,
                  hasBenefit: true,
                  status: 'AVAILABLE',
                  type: 'HEALTHCARE',
                  months,
                  monthsToAllocate: numberOfMonths,
                  reimbursementRequests: [],
                  releasedDate: new Date().toISOString(),
                  detail: {
                    total: totalAmount,
                    amountSpent: 0,
                    amountPending: 0,
                    amountRemaining: totalAmount,
                    amountForfeited: 0
                  }
                };
              }
            }
            
            configureDemoCase({ variables });
          }} 
          disabled={loading}
          className="font-inter"
          sx={{ mt: 2 }}
        >
          {loading ? 'Configuring...' : 'Configure Demo Case'}
        </Button>

        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="demo-case-modal-title"
          aria-describedby="demo-case-modal-description"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography id="demo-case-modal-title" variant="h6" component="h2">
                Demo Case Details
              </Typography>
              <IconButton onClick={handleClose} size="small">
                <CloseOutlined />
              </IconButton>
            </Box>

            {caseDetails && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>Name:</strong> {caseDetails.contact?.firstName} {caseDetails.contact?.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>Zipcode:</strong> {caseDetails.contact?.address?.zip}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>Date of Birth:</strong> {caseDetails.contact?.dateOfBirth}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>{caseType === CaseTypes.Ichra ? 'ICHRA Contribution' : 'When Benefit'} Amount:</strong> ${caseDetails.whenBenefit?.amount?.toFixed(2) || '0.00'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>Staging Case URL:</strong>{' '}
                    <Link href={getStagingCaseUrl(caseDetails.companyId, caseDetails.caseId)} target="_blank" rel="noopener">
                      {getStagingCaseUrl(caseDetails.companyId, caseDetails.caseId)}
                    </Link>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>Demo Account Creation Page:</strong>{' '}
                    <Link href={`${getLovableCaseUrl(caseDetails.companyId, caseDetails.caseId)}`} target="_blank" rel="noopener">
                      {getLovableCaseUrl(caseDetails.companyId, caseDetails.caseId)}
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>
        </Modal>
      </Paper>
    </Box>
  );
};

export default DemoConfiguration; 